import { Button, Col, Row } from 'react-bootstrap';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import applicationDetailSignal from '../../ApplicationDetail.signal';
import {
  fetchApplicationDetails,
  updateApplicationDetails,
  syncApplicationDetailLoanDisplayFromDetailsPayload,
  fetchEdoUsers,
  fetchLenderContacts,
  fetchRegisteredLendersForDetailsSelect,
} from '../../ApplicationDetail.helpers';
import type { LenderContactOption, RegisteredLenderOption } from '../../ApplicationDetail.helpers';
import loaderSignal from 'signals/Loader.signal';
import alertSignal from 'signals/Alert.signal';
import userAccountSignal from 'signals/UserAccount.signal';
import $appSettings from 'signals/AppSettings.signal';
import { PROGRAM_KEYS } from 'components/global/Constant/constants';
import { isEdoAdmin } from 'libs/edoPermissions';
import {
  isTrueOcrApplication,
  resolveApplicationIntakeKind,
  applicationIntakeKindLabel,
} from './applicationDetailsIntake';
import {
  resolveApplicationDetailsProgramKind,
  applicationDetailsProgramKindLabel,
} from './applicationDetailsProgram';
import CspApplicationDetailsForm from './CspApplicationDetailsForm';
import SmallBusinessApplicationDetailsForm from './SmallBusinessApplicationDetailsForm';
import type { ApplicationDetails } from './applicationDetailsViewModel';
import {
  AMOUNT_FIELDS,
  CALCAP_PERCENT_POINT_FIELDS,
  PERCENTAGE_FIELDS,
  applyCalcapOcrFinancialOverlay,
  applyBusinessTableAddressFallback,
  hydrateSharedBusinessDisplayFields,
  applyCpcfaBusinessDataOverlay,
  applyPersistedCspAppNumber,
  applyFormDataProcessingOverlay,
  buildUpdatedOcrCalcapFormData,
  buildUpdatedOcrProcessingFormData,
  flattenApplicationDetails,
  hydrateLenderOtherOrganizationIdForDetailsTab,
  getApplicationDetailsFieldCategory,
  getFormDataAsRecord,
  organizeApplicationDetailsFields,
  sacReasonImpliesSeverelyAffected,
  SEDI_WIREFRAME_ORDER,
  SAC_SUPPLEMENTAL_WIREFRAME_ORDER,
  CHANGE_IN_TERMS_WIREFRAME_ORDER,
  OTHER_INFORMATION_WIREFRAME_ORDER,
  OTHER_INFORMATION_CONTINUED_ORDER,
} from './applicationDetailsViewModel';
import {
  ACTION_BUSINESS_DETAILS_KEY,
  BIZDATA_LOAN_MIRROR_KEYS,
  BUSINESS_DATA_DETAIL_EXTRA_KEYS,
  BUSINESS_DETAIL_EXTRA_KEYS,
  DISPLAY_LENDER_CALCAP_ID_KEY,
  SHARED_BUSINESS_PROFILE_LABELS,
  FRED_DETAIL_BUSINESS_DATA_KEYS,
  LENDER_DETAIL_EXTRA_KEYS,
  WIRE_FRAME_BUSINESS_EXTRA_KEYS,
  WIRE_FRAME_LENDER_DISPLAY_KEYS,
} from './applicationDetailsWireframeKeys';
import { useApplicationDetailsFieldRenderer } from './ApplicationDetailsFieldRenderer';

const READ_ONLY_FIELDS = new Set([
  'id',
  'loaId',
  'createdDate',
  'updatedDate',
  'businessId',
  /** Basic Information: not editable on this tab (workflow). */
  'status',
  'lenderId',
  'fundingProgramId',
  'programName',
  'rawOcr',
  'cpcfaImportBlob',
  'technicalAssistanceId',
  ...WIRE_FRAME_LENDER_DISPLAY_KEYS.filter((k) => k !== DISPLAY_LENDER_CALCAP_ID_KEY),
  ACTION_BUSINESS_DETAILS_KEY,
  ...BIZDATA_LOAN_MIRROR_KEYS,
]);

/** Hidden on OCR applications (captured via digital form / OCR, not these detail fields). */
const OCR_HIDDEN_BUSINESS_DATA_FIELDS = new Set<string>([]);

const EXCLUDED_FIELDS = new Set([
  'business',
  'lender',
  'fundingProgram',
  'fundingProgramMembership',
  'fundingProgramMembershipId',
  /** Replaced by Severely Affected checkbox + SAC Reason (LOA_SEV_AFFECT_COM). */
  'formData_inSacOrSedi',
  /** Shown on lender profile / elsewhere; not on Application Details wireframe. */
  'lenderEin',
  // Veteran fields live in Demographics (Y/N/D dropdowns); hide duplicate Business Data boolean checkboxes
  'businessData_veteranOwned',
  'businessData_disabledVeteran',
  'blockchain',
  'lockUser',
  'lockUserId',
  'lockStartDate',
  'edoUnreadDate',
  'edoUnread',
  'lenderUnread',
  'disbursements',
  'lossClaims',
  'loan',
  'reimbursements',
  'transactions',
  'watchables',
  'stats',
  'events',
  'cspInfos',
  'funds',
  'arbDetails',
  'comments',
  'applicationConfig',
  'approvalChainConfig',
  'disbursementConfig',
  'approvalChainConfirmations',
  'formData',
  'notes',
  'supportingDocuments',
  // businessData, demographics, and sedi are handled separately as individual fields
  'businessData',
  'demographics',
  'sedi',
]);

function normalizeDetailsDateForCompare(value: unknown): string | null {
  if (value == null || value === '') return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().split('T')[0];
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    return trimmed.includes('T') ? trimmed.split('T')[0] : trimmed;
  }
  return null;
}

function detailsDateValuesEqual(a: unknown, b: unknown): boolean {
  return normalizeDetailsDateForCompare(a) === normalizeDetailsDateForCompare(b);
}

function isDetailsTabDateFieldKey(key: string): boolean {
  return key.toLowerCase().includes('date');
}

const ApplicationDetailsTab: React.FC = () => {
  const {
    id: applicationId,
    status,
    modelType,
    applicationConfig,
    rawOcr,
  } = applicationDetailSignal.value;
  const { APPLICATION_STATUS, PORTAL_TYPES } = $appSettings.value.constants;
  const portalType = userAccountSignal.value.userData?.account?.portalType;
  const userData = userAccountSignal.value.userData;
  const canEditChangeInTerms = isEdoAdmin(userData);
  const lenderApplicationDetailsReadOnly =
    portalType === PORTAL_TYPES.lender &&
    modelType === 'application' &&
    status !== APPLICATION_STATUS.inProgress;
  const effectiveReadOnlyFields = useMemo(() => {
    const fields = new Set(READ_ONLY_FIELDS);
    if (!canEditChangeInTerms) {
      CHANGE_IN_TERMS_WIREFRAME_ORDER.forEach((k) => fields.add(k));
    }
    return fields;
  }, [canEditChangeInTerms]);
  const { isContentLoading } = loaderSignal.value;
  const [applicationDetails, setApplicationDetails] = useState<ApplicationDetails | null>(null);
  const [draftDetails, setDraftDetails] = useState<ApplicationDetails>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [edoUsers, setEdoUsers] = useState<any[]>([]);
  const [lenderContacts, setLenderContacts] = useState<LenderContactOption[]>([]);
  const [registeredLendersForSelect, setRegisteredLendersForSelect] = useState<RegisteredLenderOption[]>(
    []
  );
  const [focusedAmountFields, setFocusedAmountFields] = useState<Set<string>>(new Set());
  const [amountFieldRawValues, setAmountFieldRawValues] = useState<Record<string, string>>({});
  const [focusedPercentageFields, setFocusedPercentageFields] = useState<Set<string>>(new Set());
  const [percentageFieldRawValues, setPercentageFieldRawValues] = useState<Record<string, string>>({});
  const [focusedCalcapPercentPointFields, setFocusedCalcapPercentPointFields] = useState<Set<string>>(new Set());
  const [calcapPercentPointRawValues, setCalcapPercentPointRawValues] = useState<Record<string, string>>({});
  const isOcrApplication = useMemo(
    () =>
      isTrueOcrApplication({
        applicationConfig: applicationDetails?.applicationConfig ?? applicationConfig,
        rawOcr: applicationDetails?.rawOcr ?? rawOcr,
        cpcfaImportBlob: applicationDetails?.cpcfaImportBlob,
      }),
    [applicationConfig, rawOcr, applicationDetails]
  );

  const programKind = useMemo(
    () => resolveApplicationDetailsProgramKind(applicationDetails),
    [applicationDetails]
  );

  const intakeKind = useMemo(
    () =>
      resolveApplicationIntakeKind({
        applicationConfig: applicationDetails?.applicationConfig ?? applicationConfig,
        rawOcr: applicationDetails?.rawOcr ?? rawOcr,
        cpcfaImportBlob: applicationDetails?.cpcfaImportBlob,
      }),
    [applicationConfig, rawOcr, applicationDetails]
  );

  const isCalcapProgramForDetails = useCallback((): boolean => {
    const fp =
      applicationDetails?.fundingProgram ??
      applicationDetails?.fundingProgramMembership?.fundingProgram;
    return fp?.referenceProgram?.key === PROGRAM_KEYS.capitalAccess;
  }, [applicationDetails]);

  const isAmountField = useCallback(
    (fieldName: string): boolean => {
      const isCalcap = isCalcapProgramForDetails();
      /** CalCAP percent-point columns (enrollment %, lender/borrower %, combined rate) — not currency. */
      if (isCalcap && CALCAP_PERCENT_POINT_FIELDS.has(fieldName)) return false;
      /** Borrower fee ($): column is `calcapPercentage` but value is dollars — CalCAP OCR, CPCFA import, and digital form. */
      if (isCalcap && fieldName === 'calcapPercentage') return true;
      return AMOUNT_FIELDS.has(fieldName) || fieldName.toLowerCase().includes('amount');
    },
    [isCalcapProgramForDetails]
  );

  const parseCurrency = useCallback((value: string): number | null => {
    if (!value || value.trim() === '') return null;
    const cleaned = value.replace(/[$,\s]/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? null : parsed;
  }, []);

  const loadEdoUsers = useCallback(async () => {
    try {
      const users = await fetchEdoUsers();
      setEdoUsers(users);
    } catch (error: any) {
      console.error('Error loading EDO users:', error);
    }
  }, []);

  useEffect(() => {
    if (!applicationDetails) return;
    const lid = draftDetails?.lenderId ?? applicationDetails?.lenderId;
    if (lid === null || lid === undefined || lid === '' || Number.isNaN(Number(lid))) {
      setLenderContacts([]);
      return;
    }
    let cancelled = false;
    const idNum = Number(lid);
    (async () => {
      const rows = await fetchLenderContacts(idNum);
      if (!cancelled) setLenderContacts(rows);
    })();
    return () => {
      cancelled = true;
    };
  }, [applicationDetails, draftDetails?.lenderId, applicationDetails?.lenderId]);

  useEffect(() => {
    if (portalType !== PORTAL_TYPES.edo || applicationId == null) {
      setRegisteredLendersForSelect([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const rows = await fetchRegisteredLendersForDetailsSelect();
      if (!cancelled) setRegisteredLendersForSelect(rows);
    })();
    return () => {
      cancelled = true;
    };
  }, [portalType, PORTAL_TYPES.edo, applicationId]);

  const loadApplicationDetails = useCallback(async () => {
    if (!applicationId) return;

    try {
      loaderSignal.update({ isContentLoading: true });
      const details = await fetchApplicationDetails(applicationId);

      // Flatten businessData, demographics, and sedi into individual fields
      const flattenedDetails = { ...details };
      const loaIdValue = details?.cpcfaImportBlob?.LOA_ID;
      flattenedDetails.loaId = loaIdValue !== undefined && loaIdValue !== null
        ? String(loaIdValue)
        : null;
      if (details.businessData) {
        Object.entries(details.businessData).forEach(([key, value]) => {
          if (key !== 'id' && key !== 'applicationId' && key !== 'createdDate' && key !== 'updatedDate') {
            flattenedDetails[`businessData_${key}`] = value;
          }
        });
      }
      if (details.demographics) {
        Object.entries(details.demographics).forEach(([key, value]) => {
          if (key !== 'id' && key !== 'applicationId' && key !== 'createdDate' && key !== 'updatedDate') {
            flattenedDetails[`demographics_${key}`] = value;
          }
        });
      }
      if (details.sedi) {
        Object.entries(details.sedi).forEach(([key, value]) => {
          if (key !== 'id' && key !== 'applicationId' && key !== 'createdDate' && key !== 'updatedDate') {
            flattenedDetails[`sedi_${key}`] = value;
          }
        });
      }

      const { applicationConfig: cfgFromSignal, rawOcr: rawOcrFromSignal } =
        applicationDetailSignal.value;
      const fp =
        flattenedDetails.fundingProgram ??
        flattenedDetails.fundingProgramMembership?.fundingProgram;
      /** CalCAP: merge loan % / fee fields from formData for OCR, CPCFA import, and digital form. */
      if (fp?.referenceProgram?.key === PROGRAM_KEYS.capitalAccess) {
        applyCalcapOcrFinancialOverlay(flattenedDetails);
      }
      applyFormDataProcessingOverlay(flattenedDetails);
      applyCpcfaBusinessDataOverlay(flattenedDetails);
      applyPersistedCspAppNumber(flattenedDetails);
      applyBusinessTableAddressFallback(flattenedDetails);
      hydrateSharedBusinessDisplayFields(flattenedDetails);
      hydrateLenderOtherOrganizationIdForDetailsTab(flattenedDetails);

      setFocusedPercentageFields(new Set());
      setPercentageFieldRawValues({});
      setFocusedCalcapPercentPointFields(new Set());
      setCalcapPercentPointRawValues({});
      setApplicationDetails(flattenedDetails);
      setDraftDetails(flattenedDetails);
      setHasChanges(false);
      setFieldErrors({});
    } catch (error: any) {
      console.error('Error loading application details:', error);
      alertSignal.update({
        message: error.message || 'Failed to load application details',
        variant: 'danger',
        type: 'notification',
      });
    } finally {
      loaderSignal.update({ isContentLoading: false });
    }
  }, [applicationId]);

  useEffect(() => {
    if (applicationId) {
      loadApplicationDetails();
    }
    loadEdoUsers();
  }, [applicationId, loadApplicationDetails, loadEdoUsers]);

  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    setDraftDetails((prev) => {
      const next: ApplicationDetails = { ...prev, [fieldName]: value };
      if (fieldName === 'formData_sacReason') {
        next.businessData_severelyAffected = sacReasonImpliesSeverelyAffected(value);
      }
      if (fieldName === 'lenderId') {
        next.formData_lenderContactId = null;
      }
      if (fieldName === 'sedi_businessBuild' && typeof value === 'boolean') {
        next.sedi_businessBuildArea = value;
      }
      return next;
    });

    if (fieldErrors[fieldName]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  }, [fieldErrors]);

  useEffect(() => {
    if (!applicationDetails) return;

    const hasChanges = Object.keys(draftDetails).some((key) => {
      if (effectiveReadOnlyFields.has(key) || EXCLUDED_FIELDS.has(key)) return false;
      const orig = applicationDetails[key];
      const draft = draftDetails[key];
      if (key.startsWith('businessData_') || key.startsWith('demographics_') || key.startsWith('sedi_')) {
        if (isDetailsTabDateFieldKey(key)) {
          return !detailsDateValuesEqual(orig, draft);
        }
        return orig !== draft;
      }
      if (isDetailsTabDateFieldKey(key)) {
        return !detailsDateValuesEqual(orig, draft);
      }
      return JSON.stringify(orig) !== JSON.stringify(draft);
    });

    setHasChanges(hasChanges);
  }, [draftDetails, applicationDetails, effectiveReadOnlyFields]);

  const handleSave = useCallback(async () => {
    if (!applicationId || !hasChanges || lenderApplicationDetailsReadOnly) return;

    setIsSaving(true);
    setFieldErrors({});

    try {
      const processedDraftDetails = { ...draftDetails };
      focusedAmountFields.forEach((fieldName) => {
        if (isAmountField(fieldName)) {
          const rawValue = amountFieldRawValues[fieldName] ?? '';
          const numValue = parseCurrency(rawValue);
          if (numValue !== null && !isNaN(numValue)) {
            processedDraftDetails[fieldName] = numValue;
          }
        }
      });

      PERCENTAGE_FIELDS.forEach((fieldName) => {
        if (isCalcapProgramForDetails() && fieldName === 'calcapPercentage') return;
        if (percentageFieldRawValues[fieldName] === undefined) return;
        const cleaned = (percentageFieldRawValues[fieldName] ?? '').replace(/[%\s]/g, '');
        if (cleaned === '') {
          processedDraftDetails[fieldName] = null;
          return;
        }
        const parsed = parseFloat(cleaned);
        if (!isNaN(parsed)) {
          processedDraftDetails[fieldName] = parsed > 1 ? parsed / 100 : parsed;
        }
      });

      if (isCalcapProgramForDetails()) {
        focusedCalcapPercentPointFields.forEach((fieldName) => {
          const raw = calcapPercentPointRawValues[fieldName];
          if (raw === undefined) return;
          const cleaned = raw.replace(/[%\s]/g, '');
          if (cleaned === '') {
            processedDraftDetails[fieldName] = null;
            return;
          }
          const parsed = parseFloat(cleaned);
          if (!isNaN(parsed)) {
            processedDraftDetails[fieldName] = parsed;
          }
        });
      }

      const changedFields: Record<string, any> = {};
      Object.keys(processedDraftDetails).forEach((key) => {
        if (effectiveReadOnlyFields.has(key) || EXCLUDED_FIELDS.has(key)) return;

        const originalValue = applicationDetails?.[key];
        const draftValue = processedDraftDetails[key];

        if (key.startsWith('businessData_') || key.startsWith('demographics_') || key.startsWith('sedi_')) {
          if (isDetailsTabDateFieldKey(key)) {
            if (!detailsDateValuesEqual(originalValue, draftValue)) {
              changedFields[key] = draftValue;
            }
          } else if (originalValue !== draftValue) {
            changedFields[key] = draftValue;
          }
        } else if (isDetailsTabDateFieldKey(key)) {
          if (!detailsDateValuesEqual(originalValue, draftValue)) {
            changedFields[key] = draftValue;
          }
        } else if (JSON.stringify(originalValue) !== JSON.stringify(draftValue)) {
          changedFields[key] = draftValue;
        }
      });

      if (isCalcapProgramForDetails()) {
        const updatedFormData = buildUpdatedOcrCalcapFormData(
          processedDraftDetails.formData ?? applicationDetails?.formData,
          processedDraftDetails
        );
        const originalFormData = getFormDataAsRecord(
          applicationDetails?.formData
        ) ?? {};
        if (JSON.stringify(updatedFormData) !== JSON.stringify(originalFormData)) {
          changedFields.formData = updatedFormData;
        }
      }

      {
        const baseFormData =
          changedFields.formData ??
          processedDraftDetails.formData ??
          applicationDetails?.formData;
        const updatedProcessingFormData = buildUpdatedOcrProcessingFormData(
          baseFormData,
          processedDraftDetails
        );
        const originalProcessingFormData = getFormDataAsRecord(
          applicationDetails?.formData
        ) ?? {};
        if (JSON.stringify(updatedProcessingFormData) !== JSON.stringify(originalProcessingFormData)) {
          changedFields.formData = updatedProcessingFormData;
        }

        delete changedFields.formData_businessOrgType;
        delete changedFields.formData_businessCounty;
        delete changedFields.formData_averageAnnualRevenueLast3Years;
        delete changedFields.formData_inSacOrSedi;
        delete changedFields.formData_sacReason;
        delete changedFields.formData_loanStartDate;
        delete changedFields.formData_interestRateType;
        delete changedFields.formData_usedAtAddress;
        delete changedFields.formData_calcapLoanNumPrior;
        delete changedFields.formData_remainBalanceRestructure;
        delete changedFields.formData_amountIncreaseRequested;
        delete changedFields.formData_cspAppNumber;
        delete changedFields.formData_lenderContactId;
        delete changedFields.formData_sediCircumstances;
        delete changedFields.formData_sediBusinessOwnedAddress;
        delete changedFields.formData_sediBusinessBuildAddress;
        delete changedFields.formData_cdfiInvestmentAreaAddress;
        delete changedFields.formData_sacQualifyingEvent;
      }

      if (Object.keys(changedFields).length === 0) {
        setIsSaving(false);
        alertSignal.update({
          message: 'No changes to save',
          variant: 'info',
          type: 'notification',
        });
        return;
      }

      const isBusinessEinChanging = 'businessEin' in changedFields;
      const isBusinessDataEinChanging = 'businessData_ein' in changedFields;
      const isLenderEinChanging = 'lenderEin' in changedFields;

      if (isBusinessEinChanging || isBusinessDataEinChanging || isLenderEinChanging) {
        const messages: string[] = [];
        if (isBusinessEinChanging || isBusinessDataEinChanging) {
          messages.push('Business EIN');
        }
        if (isLenderEinChanging) {
          messages.push('Lender EIN');
        }

        const confirmMessage = `Updating ${messages.join(' and ')} will also update the related ${messages.length > 1 ? 'Business and Lender' : messages[0].includes('Business') ? 'Business' : 'Lender'} record${messages.length > 1 ? 's' : ''}. Do you want to continue?`;

        if (!window.confirm(confirmMessage)) {
          setIsSaving(false);
          return;
        }
      }

      const isLenderIdChanging =
        'lenderId' in changedFields &&
        Number(applicationDetails?.lenderId) !== Number(changedFields.lenderId);

      if (isLenderIdChanging) {
        if (
          !window.confirm(
            'Change this application to the selected registered lender? Lender contacts and displayed lender information will reflect the new lender after save.'
          )
        ) {
          setIsSaving(false);
          return;
        }
      }

      const sharedBusinessLabels = Object.entries(SHARED_BUSINESS_PROFILE_LABELS)
        .filter(([key]) => key in changedFields)
        .map(([, label]) => label);
      if (sharedBusinessLabels.length > 0) {
        if (
          !window.confirm(
            `Saving ${sharedBusinessLabels.join(', ')} updates the Business record. Every loan tied to this business will show the new values. Do you want to continue?`
          )
        ) {
          setIsSaving(false);
          return;
        }
      }

      const updatedDetails = await updateApplicationDetails(applicationId, changedFields);

      const updatedDetailsInDollars = { ...updatedDetails };
      Object.keys(updatedDetailsInDollars).forEach((key) => {
        if (isAmountField(key)) {
          const value = updatedDetailsInDollars[key];
          if (value !== null && value !== undefined) {
            const numValue = typeof value === 'number' ? value : Number(value);
            if (!isNaN(numValue) && isFinite(numValue)) {
              updatedDetailsInDollars[key] = numValue;
            }
          }
        }
      });

      const flattenedDetails = flattenApplicationDetails(updatedDetailsInDollars);
      applyFormDataProcessingOverlay(flattenedDetails);
      applyCpcfaBusinessDataOverlay(flattenedDetails);
      applyPersistedCspAppNumber(flattenedDetails);
      applyBusinessTableAddressFallback(flattenedDetails);
      hydrateSharedBusinessDisplayFields(flattenedDetails);

      setFocusedAmountFields(new Set());
      setAmountFieldRawValues({});
      setFocusedPercentageFields(new Set());
      setPercentageFieldRawValues({});
      setFocusedCalcapPercentPointFields(new Set());
      setCalcapPercentPointRawValues({});
      setApplicationDetails(flattenedDetails);
      setDraftDetails(flattenedDetails);
      setHasChanges(false);

      syncApplicationDetailLoanDisplayFromDetailsPayload(
        flattenedDetails as Record<string, unknown>
      );

      alertSignal.update({
        message: 'Application details saved successfully',
        variant: 'success',
        type: 'notification',
      });
    } catch (error: any) {
      console.error('Error saving application details:', error);

      if (error.fieldErrors) {
        setFieldErrors(error.fieldErrors);
      }

      alertSignal.update({
        message: error.message || 'Failed to save application details',
        variant: 'danger',
        type: 'notification',
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    applicationId,
    hasChanges,
    draftDetails,
    applicationDetails,
    lenderApplicationDetailsReadOnly,
    effectiveReadOnlyFields,
    focusedAmountFields,
    amountFieldRawValues,
    percentageFieldRawValues,
    focusedCalcapPercentPointFields,
    calcapPercentPointRawValues,
    isCalcapProgramForDetails,
    isAmountField,
    parseCurrency,
  ]);

  const handleReset = useCallback(() => {
    if (applicationDetails) {
      setDraftDetails(applicationDetails);
      setHasChanges(false);
      setFieldErrors({});
      setFocusedPercentageFields(new Set());
      setPercentageFieldRawValues({});
      setFocusedCalcapPercentPointFields(new Set());
      setCalcapPercentPointRawValues({});
    }
  }, [applicationDetails]);

  const getFundingProgram = useCallback(() => {
    const directProgram = applicationDetails?.fundingProgram;
    const membershipProgram = applicationDetails?.fundingProgramMembership?.fundingProgram;
    return directProgram || membershipProgram;
  }, [applicationDetails]);

const { renderField } = useApplicationDetailsFieldRenderer({
    programKind,
    excludedFields: EXCLUDED_FIELDS,
    readOnlyFields: effectiveReadOnlyFields,
    draftDetails,
    applicationDetails,
    fieldErrors,
    isContentLoading,
    isSaving,
    edoUsers,
    lenderContacts,
    registeredLendersForSelect,
    getFundingProgram,
    isAmountField,
    parseCurrency,
    handleFieldChange,
    amountFieldRawValues,
    setAmountFieldRawValues,
    focusedAmountFields,
    setFocusedAmountFields,
    focusedPercentageFields,
    setFocusedPercentageFields,
    percentageFieldRawValues,
    setPercentageFieldRawValues,
    focusedCalcapPercentPointFields,
    setFocusedCalcapPercentPointFields,
    calcapPercentPointRawValues,
    setCalcapPercentPointRawValues,
    lenderApplicationDetailsReadOnly,
    APPLICATION_STATUS,
    portalType,
    PORTAL_TYPES,
  });

  const isApprovedApplication =
    status === APPLICATION_STATUS.approved ||
    applicationDetails?.status === APPLICATION_STATUS.approved;

  const fields = useMemo(() => {
    if (!applicationDetails) return [];
    const allFields = Array.from(
      new Set([
        ...Object.keys(applicationDetails)
          .filter((key) => !EXCLUDED_FIELDS.has(key))
          .filter(
            (key) =>
              !isOcrApplication || !OCR_HIDDEN_BUSINESS_DATA_FIELDS.has(key)
          ),
        ...FRED_DETAIL_BUSINESS_DATA_KEYS,
        ...LENDER_DETAIL_EXTRA_KEYS,
        ...BUSINESS_DETAIL_EXTRA_KEYS,
        ...WIRE_FRAME_LENDER_DISPLAY_KEYS,
        ...WIRE_FRAME_BUSINESS_EXTRA_KEYS,
        ...BUSINESS_DATA_DETAIL_EXTRA_KEYS,
        ...SEDI_WIREFRAME_ORDER,
        ...SAC_SUPPLEMENTAL_WIREFRAME_ORDER,
        ...OTHER_INFORMATION_WIREFRAME_ORDER,
        ...OTHER_INFORMATION_CONTINUED_ORDER,
        ...(isApprovedApplication ? CHANGE_IN_TERMS_WIREFRAME_ORDER : []),
        'programName',
        ...(programKind === 'capitalAccess' ? ['calcapLoanNumber', 'lenderLoanNumber'] : []),
      ])
    )
      .filter((key) => key !== 'sedi_businessBuildArea')
      .filter(
        (key) =>
          isApprovedApplication || !CHANGE_IN_TERMS_WIREFRAME_ORDER.includes(key)
      );
    return organizeApplicationDetailsFields(allFields, programKind);
  }, [applicationDetails, isOcrApplication, programKind, isApprovedApplication]);

  if (isContentLoading && !applicationDetails) {
    return (
      <div className="text-center py-5">
        <div>Loading application details...</div>
      </div>
    );
  }

  if (!applicationDetails) {
    return (
      <div className="text-center py-5">
        <div className="text-muted">No application details found.</div>
      </div>
    );
  }

  const DetailsFormShell =
    programKind === 'csp' ? CspApplicationDetailsForm : SmallBusinessApplicationDetailsForm;

  return (
    <div>
      <Row className="mb-16">
        <Col>
          <h5>Application Details</h5>
          <p className="text-muted">
            {lenderApplicationDetailsReadOnly
              ? 'Application details can only be edited while the application is a draft. This application has been submitted or advanced; details are view-only.'
              : 'Edit application fields below. Changes will be saved when you click "Save Changes".'}
          </p>
          <p className="text-muted small mb-0">
            {applicationDetailsProgramKindLabel(programKind)} · {applicationIntakeKindLabel(intakeKind)}
          </p>
        </Col>
        <Col className="text-right">
          <Button
            variant="secondary"
            className="rounded mr-2"
            onClick={handleReset}
            disabled={
              lenderApplicationDetailsReadOnly ||
              !hasChanges ||
              isSaving ||
              isContentLoading
            }
          >
            Reset
          </Button>
          <Button
            variant="primary"
            className="rounded"
            onClick={handleSave}
            disabled={
              lenderApplicationDetailsReadOnly ||
              !hasChanges ||
              isSaving ||
              isContentLoading
            }
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Col>
      </Row>

      <DetailsFormShell>
      <Row>
        {fields.map((fieldName, index) => {
          const currentCategory = getApplicationDetailsFieldCategory(fieldName, programKind);
          const previousField = index > 0 ? fields[index - 1] : null;
          const previousCategory = previousField
            ? getApplicationDetailsFieldCategory(previousField, programKind)
            : null;
          const showSectionHeader = currentCategory !== previousCategory;

          const isLoanDetailsCheckbox = currentCategory === 'loanDetails' && (fieldName === 'refinancing' || fieldName === 'secured');
          /** Wireframe order: Loan Secured, then Restructure of Prior CalCAP Loan — render both on the first of the pair. */
          const isFirstLoanDetailsCheckbox = isLoanDetailsCheckbox && fieldName === 'secured';
          const isSecondLoanDetailsCheckbox = isLoanDetailsCheckbox && fieldName === 'refinancing';

          const categoryLabels: Record<string, string> = {
            basic: 'Basic Information',
            business: 'Business Information',
            lender: 'Lender Information',
            program: 'Program Information',
            dates: 'Important Dates',
            financial: 'Financial Information',
            loanDetails: 'Loan Details',
            premiums: 'Premiums',
            jobs: 'Jobs Information',
            flags: 'Status Flags',
            delivery: 'Delivery Information',
            guarantee: 'Guarantee Information',
            businessData: 'Business Data as of this Application',
            demographics: 'Demographics',
            sedi: 'SEDI',
            sacSupplemental: 'SAC Supplemental',
            other: 'Other Information',
            otherContinued: 'Other Information',
            changeInTerms: 'Change in Terms',
          };

          return (
            <React.Fragment key={`${fieldName}-${index}`}>
              {showSectionHeader && (
                <Col md={12} className="mt-4 mb-3">
                  <h6 className="text-muted border-bottom pb-2 mb-3" style={{ fontWeight: 600 }}>
                    {categoryLabels[currentCategory]}
                  </h6>
                </Col>
              )}
              {isFirstLoanDetailsCheckbox && (
                <>
                  {renderField('secured', applicationDetails['secured'])}
                  {fields.includes('refinancing') && renderField('refinancing', applicationDetails['refinancing'])}
                  <Col md={12} />
                </>
              )}
              {fieldName === 'loanPurpose' && (
                <>
                  <Col md={12} />
                  {renderField(fieldName, applicationDetails[fieldName])}
                </>
              )}
              {fieldName === 'loanPurposeSecondary' && (
                <>
                  <Col md={12} />
                  {renderField(fieldName, applicationDetails[fieldName])}
                </>
              )}
              {fieldName === 'loanType' && (
                <>
                  <Col md={12} />
                  {renderField(fieldName, applicationDetails[fieldName])}
                </>
              )}
              {currentCategory === 'loanDetails' && fieldName === 'interestRate' && (
                <>
                  {renderField(fieldName, applicationDetails[fieldName])}
                </>
              )}
              {!isSecondLoanDetailsCheckbox &&
                !isFirstLoanDetailsCheckbox &&
                fieldName !== 'loanPurpose' &&
                fieldName !== 'loanPurposeSecondary' &&
                fieldName !== 'loanType' &&
                fieldName !== 'interestRate' &&
                renderField(fieldName, applicationDetails[fieldName])}
            </React.Fragment>
          );
        })}
      </Row>
      </DetailsFormShell>

      {hasChanges && !lenderApplicationDetailsReadOnly && (
        <Row className="mt-4">
          <Col>
            <div className="alert alert-info">
              You have unsaved changes. Click &quot;Save Changes&quot; to save or &quot;Reset&quot; to discard.
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default ApplicationDetailsTab;
